/* global Office Word, console */
import {
  getAccessToken,
  getRefreshToken,
  saveAccessToken,
  saveRefreshToken,
} from "../utils/storage.utils";
import { refreshToken } from "../services/auth.service";
import { aiDraft, AiDraftRequest, AiDraftOption } from "../services/ai.service";
import { convertMarkdownToHtml, isHtmlContent } from "../utils/markdown.utils";
import { processDraftFullDocument } from "./draft-full-document";
import { TOAST_STORAGE_KEY } from "../config/storage.config";
import {
  captureSelection,
  insertContentIntoDocument,
  releaseTrackedRange,
  type CapturedSelection,
} from "./insert-content.utils";

export { insertContentIntoDocument } from "./insert-content.utils";
export type { CapturedSelection };

const APP_URL = process.env.APP_URL ?? "https://localhost:3000";

export interface ToastMessage {
  type: "info" | "error" | "success" | "warning";
  message: string;
}

export function isUnauthorizedError(error: unknown): boolean {
  if (typeof error !== "object" || error == null || !("response" in error)) return false;
  const resp = error.response;
  return typeof resp === "object" && resp != null && "status" in resp && resp.status === 401;
}

export function sendMessageToTaskpane(message: ToastMessage): void {
  try {
    sessionStorage.setItem(
      TOAST_STORAGE_KEY,
      JSON.stringify({
        ...message,
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    console.warn("[AILA] Could not write to sessionStorage:", e);
  }
}

export function getSelectedText(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(String(result.value ?? ""));
      } else {
        reject(new Error(result.error?.message || "Failed to get selected text"));
      }
    });
  });
}

export async function openTaskpane(): Promise<void> {
  try {
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.error("Failed to show taskpane:", error);
  }
}
let dialog: Office.Dialog | null = null;

export function showLoadingDialog(): Promise<boolean> {
  return new Promise((resolve) => {
    const loadingUrl = `${APP_URL}/loading.html`;

    Office.context.ui.displayDialogAsync(
      loadingUrl,
      { height: 50, width: 50, displayInIframe: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.warn("[AILA] Could not show loading dialog:", asyncResult.error.message);
          resolve(false);
        } else {
          dialog = asyncResult.value;
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => {});
          resolve(true);
        }
      }
    );
  });
}

export function closeLoadingDialog(): void {
  if (dialog) {
    try {
      dialog.close();
    } catch {
      // Dialog may already have been dismissed by the user or by Office.js.
    } finally {
      dialog = null;
    }
  }
}

async function captureNonEmptySelection(
  completeEvent: () => void
): Promise<CapturedSelection | null> {
  let captured: CapturedSelection;
  try {
    captured = await captureSelection();
  } catch (selectionError) {
    console.error("[AILA] Error getting selected text:", selectionError);
    completeEvent();
    return null;
  }
  if (!captured.text || captured.text.trim().length === 0) {
    await releaseTrackedRange(captured.range);
    sendMessageToTaskpane({
      type: "info",
      message: "Please select some text first, then right-click to use AILA features.",
    });
    completeEvent();
    return null;
  }
  return captured;
}

export async function validatePrerequisites(
  _option: AiDraftOption,
  completeEvent: () => void
): Promise<CapturedSelection | null> {
  const officeInfo = await Office.onReady();
  if (officeInfo.host == null) {
    console.error("[AILA] Office.js not ready");
    completeEvent();
    return null;
  }
  if (officeInfo.host !== Office.HostType.Word || typeof Word === "undefined") {
    console.error("[AILA] Word API not ready");
    completeEvent();
    return null;
  }
  let token = await getAccessToken();
  if (token == null) {
    const refresh = await getRefreshToken();
    if (refresh != null) {
      try {
        const tokens = await refreshToken();
        token = tokens.access_token;
        await saveAccessToken(tokens.access_token);
        await saveRefreshToken(tokens.refresh_token);
      } catch (refreshError) {
        console.error("[AILA] Token refresh failed:", refreshError);
      }
    }

    if (token == null) {
      console.log("[AILA] No valid token. Opening taskpane for login.");
      await openTaskpane();
      sendMessageToTaskpane({ type: "error", message: "Please log in to use this feature." });
      completeEvent();
      return null;
    }
  }
  return captureNonEmptySelection(completeEvent);
}

function notifyIfTruncated(isDraftFullDocument: boolean, response: object): void {
  if (
    isDraftFullDocument &&
    "truncated" in response &&
    (response as { truncated?: boolean }).truncated === true
  ) {
    sendMessageToTaskpane({
      type: "warning",
      message:
        "Draft inserted, but the AI stopped before finishing. Review the document for completeness.",
    });
  }
}

async function draftIntoTrackedSelection(
  option: AiDraftOption,
  selectedText: string,
  targetRange: Word.Range
): Promise<void> {
  const { getDocumentInfo } = await import("../utils/document.utils");
  const documentInfo = await getDocumentInfo();
  const request: AiDraftRequest = {
    document_id: documentInfo.id ?? "",
    text_selected: selectedText,
  };
  const isDraftFullDocument = option === AiDraftOption.DraftFullDocument;
  const response = isDraftFullDocument
    ? await processDraftFullDocument(request)
    : await aiDraft(option, request);
  if (!response.response || response.response.trim().length === 0) {
    console.warn("[AILA] AI returned an empty response. Selection preserved.");
    sendMessageToTaskpane({
      type: "warning",
      message: "The AI returned an empty response. Your text was not modified.",
    });
    return;
  }
  const htmlResponse = isHtmlContent(response.response)
    ? response.response
    : convertMarkdownToHtml(response.response);
  await insertContentIntoDocument(htmlResponse, targetRange);
  notifyIfTruncated(isDraftFullDocument, response);
}

function reportDraftFailure(apiError: unknown): Promise<void> | void {
  console.error("[AILA] API Error:", apiError);
  closeLoadingDialog();
  if (isUnauthorizedError(apiError)) {
    return openTaskpane().then(() => {
      sendMessageToTaskpane({
        type: "error",
        message: "Your session has expired. Please log in again.",
      });
    });
  }
  console.error(
    "[AILA] Draft error detail:",
    apiError instanceof Error ? apiError.message : apiError
  );
  sendMessageToTaskpane({ type: "error", message: "Something went wrong. Please try again." });
}

export async function executeAiDraft(
  option: AiDraftOption,
  captured: CapturedSelection,
  completeEvent: () => void
): Promise<void> {
  await showLoadingDialog();
  try {
    await draftIntoTrackedSelection(option, captured.text, captured.range);
    closeLoadingDialog();
    completeEvent();
  } catch (apiError: unknown) {
    await reportDraftFailure(apiError);
    completeEvent();
  } finally {
    await releaseTrackedRange(captured.range);
  }
}
